import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useState, useEffect, useMemo } from "react";
import { IncentiveTagCategory } from "@/bryntum-lib/models/IncentiveTagCategory";
import { IncentiveTag } from "@/bryntum-lib/models/IncentiveTag";
import { toast } from "sonner";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { ColorPicker } from "@/modern/components/color-picker";
import { Combobox } from "@/modern/components/ui/combobox";
import { Card } from "@/modern/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { Badge } from "@/modern/components/ui/badge";
import { AlertCircle } from "lucide-react";


const getRandomColor = () => {
  return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
}

// ============================================================================
// Main Component
// ============================================================================

const IncentiveCategoriesAndTagsDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user } = useSwivuBryntumContext();
  const [categories, setCategories] = useState<IncentiveTagCategory[]>([]);
  const [tags, setTags] = useState<IncentiveTag[]>([]);

  const canEdit = useMemo<boolean>(() => {
    if (!user) return false;
    return user.can(GRANTS.grant_one);
  }, [user]);

  const { subscribe: subscribeCategories } = useSwivuStore({
    storeId: STORE_KEYS.incentiveTagCategoryStore,
  });

  const { subscribe: subscribeTags } = useSwivuStore({
    storeId: STORE_KEYS.incentiveTagsStore,
  });

  const loadCategories = () => {
    if (!project) return;
    const categoryStore = project.incentiveTagCategoryStore;
    const categoryRecords = categoryStore.query(() => true, true) || [];
    setCategories(prev => {
      const storeIds = new Set(categoryRecords.map((c: IncentiveTagCategory) => c.id));
      const phantomsToKeep = prev.filter(c => c.isPhantom && !storeIds.has(c.id));
      return [...categoryRecords, ...phantomsToKeep];
    });
  };

  const loadTags = () => {
    if (!project) return;
    const tagStore = project.incentiveTagsStore;
    const tagRecords = tagStore.query(() => true, true) || [];
    setTags(prev => {
      const storeIds = new Set(tagRecords.map((t: IncentiveTag) => t.id));
      const phantomsToKeep = prev.filter(t => t.isPhantom && !storeIds.has(t.id));
      return [...tagRecords, ...phantomsToKeep];
    });
  };

  useEffect(() => {
    loadCategories();
    return subscribeCategories(() => {
      loadCategories();
      loadTags();
    });
  }, [subscribeCategories]);

  useEffect(() => {
    loadTags();
    return subscribeTags(() => {
      loadTags();
    });
  }, [subscribeTags]);

  return (
    <div className="p-6 flex flex-col w-full h-full space-y-4 pb-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      {/* Categories Section */}
      <CategoriesSection
        categories={categories}
        canEdit={canEdit}
        setCategories={setCategories}
      />

      {/* Tags Section */}
      <TagsSection
        tags={tags}
        categories={categories.filter(category => !category.isPhantom)}
        canEdit={canEdit}
        setTags={setTags}
      />
    </div>
  );
};


// ============================================================================
// Categories Section Component
// ============================================================================

interface CategoriesSectionProps {
  categories: IncentiveTagCategory[];
  canEdit: boolean;
  setCategories: (categories: IncentiveTagCategory[]) => void;
}

const CategoriesSection = ({ categories, canEdit, setCategories }: CategoriesSectionProps) => {
  const { project } = useSwivuBryntumContext();


  const persistCategory = (category: IncentiveTagCategory) => {
    if (category.isPhantom) {
      if (category.name.trim() && category.color && category.points !== undefined && category.points !== null && category.points >= 0) {
        project.incentiveTagCategoryStore.add(category);
      }
    }
  };

  const handleUpdateCategoryField = (categoryRecord: IncentiveTagCategory, field: string, value: any) => {
    if (!project) return false;

    // Validate based on field
    if (field === "name") {
      const name = value?.trim() || "";
      if (!name) {
        toast.error("Name is required");
        return false;
      }
      const isDuplicate = categories.some(c =>
        c.id !== categoryRecord.id && c.name.trim().toLowerCase() === name.toLowerCase()
      );
      if (isDuplicate) {
        toast.error("Category with same name already exists");
        return false;
      }
    } else if (field === "color") {
      if (!value) {
        toast.error("Color is required");
        return false;
      }
    } else if (field === "points") {
      if (value === null || value === undefined || value === "") {
        toast.error("Points are required");
        return false;
      }
      const points = Number(value);
      if (isNaN(points)) {
        toast.error("Points must be a valid number");
        return false;
      }
      if (points < 0) {
        toast.error("Points cannot be less than 0");
        return false;
      }
    }

    categoryRecord[field] = value;
    persistCategory(categoryRecord);
    return true;
  };

  const handleDeleteCategory = (category: IncentiveTagCategory) => {
    if (!project) return;
    const categoryStore = project.incentiveTagCategoryStore;
    // @ts-expect-error - category is a model
    categoryStore.remove(category);
    setCategories(categories.filter(c => c.id !== category.id));
  };

  const handleAddCategory = (value: string) => {
    if (!project) return;

    // Check for duplicate category name
    const name = value.trim();
    if (!name) {
      toast.error("Name is required");
      return;
    }

    const isDuplicate = categories.some(c =>
      c.name.trim().toLowerCase() === name.toLowerCase()
    );

    if (isDuplicate) {
      toast.error("Category with same name already exists");
      return;
    }

    const categoryStore = project.incentiveTagCategoryStore;
    const newCategory = categoryStore.createRecord({
      name: value,
      color: getRandomColor(),
    });
    setCategories([...categories, newCategory as unknown as IncentiveTagCategory]);
  };

  return (
    <Card className="p-4 space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Categories</h3>
        <p className="text-sm text-muted-foreground">
          Create categories with point values and colors to organize tags for incentives.
        </p>
      </div>
      <SwivuList.Root<IncentiveTagCategory>
        items={categories}
        searchConfig={{
          fields: ["name", "description"],
          placeholder: "Search categories...",
          inputClassName: "bg-background",
        }}
        className="overflow-visible"
      >
        <SwivuList.Toolbar />

        <SwivuList.Header>
          <div className="flex items-center w-[2.6rem]" />
          <SwivuList.HeaderItem flex className="flex-1">
            Name
          </SwivuList.HeaderItem>
          <SwivuList.HeaderItem flex className="flex-1">
            Description
          </SwivuList.HeaderItem>
          <SwivuList.HeaderItem flex className="flex-1">
            Points
          </SwivuList.HeaderItem>
          <div className="w-[2.5rem]" />
        </SwivuList.Header>

        <SwivuList.Content<IncentiveTagCategory> emptyMessage="No categories yet. Create your first category!">
          {(category: IncentiveTagCategory) => (
            <SwivuList.Item
              value={category.id as string | number}
              className={`relative ${category.isPhantom ? 'bg-amber-50/30 dark:bg-amber-950/20 border-2 border-dashed border-amber-400/60 dark:border-amber-600/60' : 'bg-background'}`}
            >
              {category.isPhantom && (
                <DraftBadge
                  text="Complete all required fields (Name, Color, Points) to save this category"
                />
              )}
              <SwivuList.ItemHandle disabled={!canEdit} value={category.id as string | number} />
              <div className="flex items-center">
                <ColorPicker
                  value={category.color}
                  onChange={(color) => handleUpdateCategoryField(category, "color", color)}
                  clearable={true}
                  triggerClassName="h-auto w-auto [&_*]:m-0 py-0 border-none px-0 bg-transparent hover:opacity-80"
                  disabled={!canEdit}
                  hideDisplayValue={true}
                />
              </div>
              <SwivuList.TextField
                item={category}
                value={category.name}
                onUpdate={(categoryRecord, newValue) => handleUpdateCategoryField(categoryRecord, "name", newValue)}
                placeholder="Enter category name"
                disabled={!canEdit}
                className="flex-1"
              />
              <SwivuList.TextField
                item={category}
                value={category.description}
                onUpdate={(categoryRecord, newValue) => handleUpdateCategoryField(categoryRecord, "description", newValue)}
                placeholder="Enter category description"
                disabled={!canEdit}
                className="flex-1"
              />
              <SwivuList.TextField
                item={category}
                value={category.points?.toString()}
                onUpdate={(categoryRecord, newValue) => handleUpdateCategoryField(categoryRecord, "points", newValue)}
                placeholder="Enter category points"
                disabled={!canEdit}
                className="flex-1"
                type="number"
              />
              <SwivuList.ItemActions
                disabled={!canEdit}
                onDelete={() => handleDeleteCategory(category)}
              />
            </SwivuList.Item>
          )}
        </SwivuList.Content>
        <SwivuList.QuickAdd
          disabled={!canEdit}
          onAdd={(value) => handleAddCategory(value)}
          placeholder="Add new category..."
          inputClassName="bg-background"
        />
      </SwivuList.Root>
    </Card>
  );
};

// ============================================================================
// Tags Section Component
// ============================================================================

interface TagsSectionProps {
  tags: IncentiveTag[];
  categories: IncentiveTagCategory[];
  canEdit: boolean;
  setTags: (tags: IncentiveTag[]) => void;
}

const TagsSection = ({ tags, categories, canEdit, setTags }: TagsSectionProps) => {
  const { project } = useSwivuBryntumContext();

  const persistTag = (tag: IncentiveTag) => {
    if (tag.isPhantom) {
      if (tag.name.trim() && tag.categoryId) {
        project.incentiveTagsStore.add(tag);
      }
    }
  };

  const handleUpdateTagField = (tagRecord: IncentiveTag, field: string, value: any) => {
    if (!project) return false;

    if (field === "name") {
      const name = value?.trim() || "";
      if (!name) {
        toast.error("Name is required");
        return false;
      }
    } else if (field === "categoryId") {
      if (!value) {
        toast.error("Category is required");
        return false;
      }
    }

    tagRecord[field] = value;
    persistTag(tagRecord);
    return true;
  };

  const handleDeleteTag = (tag: IncentiveTag) => {
    if (!project) return;
    const tagStore = project.incentiveTagsStore;
    // @ts-expect-error - tag is a model
    tagStore.remove(tag);
    setTags(tags.filter(t => t.id !== tag.id));
  };

  const handleAddTag = (value: string) => {
    if (!project) return;

    // Check for duplicate tag name
    const name = value.trim();
    if (!name) {
      toast.error("Name is required");
      return;
    }

    const isDuplicate = tags.some(t =>
      t.name.trim().toLowerCase() === name.toLowerCase()
    );

    if (isDuplicate) {
      toast.error("Tag with same name already exists");
      return;
    }

    const tagStore = project.incentiveTagsStore;
    const newTag = tagStore.createRecord({
      name: value,
    });
    setTags([...tags, newTag as unknown as IncentiveTag]);
  };

  return (
    <Card className="p-4 space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Tags</h3>
        <p className="text-sm text-muted-foreground">
          Create tags within categories for creating incentives. Categories must be created first before adding tags.
        </p>
      </div>

      <SwivuList.Root<IncentiveTag>
        items={tags}
        searchConfig={{
          fields: ["name", "description"],
          placeholder: "Search tags...",
          inputClassName: "bg-background",
        }}
        className="overflow-visible"
      >
        <SwivuList.Toolbar />

        <SwivuList.Header>
          <SwivuList.HeaderItem flex className="flex-1">
            Name
          </SwivuList.HeaderItem>
          <SwivuList.HeaderItem flex className="flex-1">
            Description
          </SwivuList.HeaderItem>
          <SwivuList.HeaderItem flex className="flex-1">
            Category
          </SwivuList.HeaderItem>
          <div className="w-[40px]" />
        </SwivuList.Header>

        <SwivuList.Content<IncentiveTag> emptyMessage="No tags yet. Create your first tag!">
          {(tag: IncentiveTag) => {
            const categoryRecord = tag.categoryRecord;
            return (
              <SwivuList.Item
                value={tag.id as string | number}
                className={`relative ${tag.isPhantom ? 'bg-amber-50/30 dark:bg-amber-950/20 border-2 border-dashed border-amber-400/60 dark:border-amber-600/60' : 'bg-background'}`}
              >
                {tag.isPhantom && (
                  <DraftBadge
                    text="Complete all required fields (Name, Category) to save this tag"
                  />
                )}
                <SwivuList.ItemHandle disabled={!canEdit} value={tag.id as string | number} />

                <SwivuList.TextField
                  item={tag}
                  value={tag.name}
                  onUpdate={(tagRecord, newValue) => handleUpdateTagField(tagRecord, "name", newValue)}
                  placeholder="Enter tag name"
                  disabled={!canEdit}
                  className="flex-1"
                />
                <SwivuList.TextField
                  item={tag}
                  value={tag.description}
                  onUpdate={(tagRecord, newValue) => handleUpdateTagField(tagRecord, "description", newValue)}
                  placeholder="Enter tag description"
                  disabled={!canEdit}
                  className="flex-1"
                />

                {/* Category */}
                <div className="flex items-center gap-2 flex-1">
                  {Boolean(categoryRecord) && (
                    <div
                      className="w-4 h-4 rounded flex-shrink-0"
                      style={{ backgroundColor: categoryRecord.color }}
                    />
                  )}
                  <Combobox
                    popoverContentClassName="w-max"
                    buttonSelectedClassName="text-sm bg-transparent p-0 !h-auto border-none w-auto min-w-0 hover:bg-transparent"
                    value={tag.categoryId?.toString() || ""}
                    onValueChange={(value) => {
                      if (value && !Array.isArray(value)) {
                        handleUpdateTagField(tag, "categoryId", value);
                      }
                    }}
                    options={categories.map(cat => ({
                      value: cat.id.toString(),
                      label: cat.name,
                      color: cat.color,
                    }))}
                    placeholder="Select category"
                    disabled={!canEdit}
                    optionItemActionRenderer={(option) => (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded flex-shrink-0 raffay testing"
                          style={{ backgroundColor: (option as any)?.color as string }}
                        />
                      </div>
                    )}
                  />
                </div>

                <SwivuList.ItemActions
                  disabled={!canEdit}
                  onDelete={() => handleDeleteTag(tag)}
                />
              </SwivuList.Item>
            );
          }}
        </SwivuList.Content>
        <SwivuList.QuickAdd
          disabled={!canEdit}
          onAdd={(value) => handleAddTag(value)}
          placeholder="Add new tag..."
          inputClassName="bg-background"
        />
      </SwivuList.Root>
    </Card>
  );
};

const DraftBadge = ({ text }: { text: string }) => {
  return (
    <div className="absolute -top-3.5 left-4 right-4 flex items-center justify-center z-10">
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div>
            <Badge
              variant="default"
              size="sm"
              className="bg-amber-500 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-700 text-white shadow-md flex items-center gap-1 px-2 py-0.5 text-xs font-semibold"
            >
              <AlertCircle className="h-3 w-3" />
              <span>DRAFT</span>
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs">{text}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export default IncentiveCategoriesAndTagsDetail;
